/**
 * Hoort dit record bij de gekozen locatie?
 *
 * Eén plek voor een vergelijking die zes keer los geschreven stond (de
 * dashboardkaarten via `useLocationFilter` en
 * `useParticipationLadderFilters`, en vier live-report-widgets), met twee
 * afgeleide varianten erbij. Zodra huishoudens er ook op filteren worden dat er
 * negen, en dan loopt het gegarandeerd uiteen.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 * AI-CONTEXT: See docs/features/huishouden-locaties.md
 */

/**
 * Het veld waarin een record zijn locatie draagt.
 *
 * **De waarde is de Firestore-`docid` van het locatiedocument, niet het
 * `ID`-veld.** Dat is andersom dan vrijwel overal elders in deze codebase, waar
 * koppelingen juist via het uuid `ID` lopen. Nagemeten in productie: alle
 * verwijzingen op `Personas` en `Tasks` kloppen als docid en geven niets als
 * `ID`. Wie hier uit gewoonte `ID` gebruikt krijgt een filter dat stil niets
 * teruggeeft — geen fout, geen waarschuwing, gewoon een lege lijst.
 *
 * @type {string}
 */
const LOCATION_KEY = 'LocationID'

/**
 * Het meervoudsveld, voor een record dat op meer dan één locatie kan zitten.
 * Zie `personaLocations.js`; huishoudens gebruiken het niet.
 *
 * @type {string}
 */
const LOCATION_KEYS = 'LocationIDs'

/**
 * Of er überhaupt een locatie gekozen is.
 *
 * Leeg betekent 'alle locaties'. Dat is met opzet de ruime kant: dit filter
 * maakt een lijst rustiger, het schermt niets af.
 *
 * @param {*} selectedLocationId
 * @returns {boolean}
 */
const hasSelection = (selectedLocationId) =>
    typeof selectedLocationId === 'string' && selectedLocationId.trim() !== ''

/**
 * @param {object|null|undefined} record - Een document met een `LocationID`
 * @param {string} [selectedLocationId] - De keuze van de gebruiker; leeg = alles
 * @returns {boolean}
 */
const recordInLocation = (record, selectedLocationId) => {
    if (!hasSelection(selectedLocationId)) return true
    if (!record || typeof record !== 'object') return false

    // Meervoud eerst: een deelnemer kan op meer locaties werken sinds de
    // werklocatie naar de roosterregel verhuisde. Een huishouden heeft er
    // precies één en draagt alleen `LocationID`.
    // AI-CONTEXT: See helpers/locations/personaLocations.js
    const meerdere = record[LOCATION_KEYS]
    if (Array.isArray(meerdere)) return meerdere.includes(selectedLocationId)

    return record[LOCATION_KEY] === selectedLocationId
}

/**
 * Hetzelfde, voor een hele lijst.
 *
 * Zonder keuze komt DEZELFDE lijst terug en niet een kopie, zodat een `useMemo`
 * verderop stabiel blijft.
 *
 * @template T
 * @param {Array<T>} list
 * @param {string} [selectedLocationId]
 * @returns {Array<T>}
 */
const filterByLocation = (list, selectedLocationId) => {
    if (!Array.isArray(list)) return []
    if (!hasSelection(selectedLocationId)) return list
    return list.filter((record) => recordInLocation(record, selectedLocationId))
}

/**
 * Hoort deze afspraak bij de gekozen locatie?
 *
 * Twee wegen, en dat is geen slordigheid maar de kern van de vraag: een
 * fixbrigade heeft fixers én huishoudens op dezelfde locatie.
 *
 * - een roosterregel draagt zijn locatie zélf (`Tasks.LocationID`, gezet op het
 *   roosterscherm);
 * - een huisbezoek draagt er geen — gemeten: 0 van de 580 — en erft hem van het
 *   huishouden waar hij bij hoort.
 *
 * Eén van de twee is genoeg. Zou je alleen naar de taak kijken, dan verdwijnt
 * elk huisbezoek uit de agenda zodra iemand filtert; alleen naar het huishouden
 * kijken laat juist alle roosterregels vallen.
 *
 * @param {object|null|undefined} task - Een document uit Tasks
 * @param {string} [selectedLocationId] - De keuze van de gebruiker; leeg = alles
 * @param {Record<string, string>} [locationByAdressId] - Huishouden-ID -> locatie
 * @returns {boolean}
 */
const taskInLocation = (task, selectedLocationId, locationByAdressId) => {
    if (!hasSelection(selectedLocationId)) return true
    if (!task || typeof task !== 'object') return false

    if (recordInLocation(task, selectedLocationId)) return true

    const viaHuishouden = (locationByAdressId || {})[task.AdressID]
    return viaHuishouden === selectedLocationId
}

/**
 * `{ huishouden-ID: locatie }`, voor het opzoeken bij een afspraak.
 *
 * Met een gewone lus en niet met `reduce` + spread: dat laatste kopieert bij
 * elke stap de hele kaart, wat kwadratisch groeit. Nagemeten op 400
 * huishoudens 7,3 ms tegen 0,11 ms, en op 3000 zelfs 808 ms tegen 0,56 ms.
 * De grootste organisatie heeft er nu ~394, dus het valt nog niet op — maar
 * dit stond op twee plekken bijna identiek en hoort er één te zijn.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @param {Array<object>} [adresses] - Documenten uit Adresses
 * @returns {Record<string, string>}
 */
const buildLocationByAdressId = (adresses) => {
    const kaart = {}

    ;(Array.isArray(adresses) ? adresses : []).forEach((adress) => {
        if (adress && adress.ID) kaart[adress.ID] = adress[LOCATION_KEY] || ''
    })

    return kaart
}

/**
 * De locaties in de volgorde die de organisatie zelf sleepte.
 *
 * `useSharedCollection` sorteert niet — dat doet alleen het instellingenscherm
 * met `useFirestoreOrderBy('Locations', 'Position', 'asc')`. Zonder deze stap
 * staat elke keuzelijst in de volgorde van Firestore, en dan negeert het scherm
 * de moeite die iemand in het slepen stak.
 *
 * Geeft een NIEUWE lijst terug; `sort` werkt ter plekke en de bron is gedeeld
 * tussen alle afnemers van de listener.
 *
 * @param {Array<object>} [locations]
 * @returns {Array<object>}
 */
const sortLocations = (locations) => (Array.isArray(locations) ? [...locations] : [])
    .sort((a, b) => (a?.Position ?? 0) - (b?.Position ?? 0))

export {
    LOCATION_KEY,
    LOCATION_KEYS,
    recordInLocation,
    filterByLocation,
    taskInLocation,
    buildLocationByAdressId,
    sortLocations,
}
